import {
  type AccordionDetailsProps,
  type AccordionSummaryProps,
  Accordion as MuiAccordion,
  AccordionDetails as MuiAccordionDetails,
  type AccordionProps as MuiAccordionProps,
  AccordionSummary as MuiAccordionSummary,
  type SxProps,
} from '@mui/material';
import { useTheme } from 'hephaestus/web';
import { useAccordionTokens } from './accordian.tokens';

type AccordionProps = Omit<MuiAccordionProps, 'variant'> & {
  multiple?: boolean;
  fullWidthOnMobile?: boolean;
  variant?: MuiAccordionProps['variant'] | 'workflow';
  state?: 'error';
};

export const Accordion = ({
  multiple = false,
  fullWidthOnMobile = false,
  state,
  ...props
}: AccordionProps) => {
  const theme = useTheme();
  const tokens = useAccordionTokens(theme);

  const defaultAccordionSx: SxProps = {
    '&.MuiAccordion-root': {
      backgroundColor:
        state === 'error' ? `${theme.palette.negative20}` : undefined,
      border:
        state === 'error'
          ? `1px solid ${theme.palette.negative40}`
          : props.variant === 'elevation'
            ? 'none'
            : `1px solid ${tokens.borderColor}`,
      marginLeft: { xs: fullWidthOnMobile ? '-16px' : 0, sm: 0 },
      marginRight: { xs: fullWidthOnMobile ? '-16px' : 0, sm: 0 },
      borderRadius: {
        xs: fullWidthOnMobile ? 0 : tokens.borderRadius,
        sm: !multiple ? tokens.borderRadius : 0,
      },
      marginBottom: props.variant === 'elevation' ? '' : 1,
      boxShadow: 'unset',
      '&:first-of-type': {
        borderRadius: {
          xs: fullWidthOnMobile
            ? 0
            : `${tokens.borderRadius} ${tokens.borderRadius} 0 0`,
          sm: `${tokens.borderRadius} ${tokens.borderRadius} 0 0`,
        },
        '.MuiAccordionSummary-root': {
          borderTop:
            props.variant === 'elevation' && multiple
              ? 'none'
              : `1px solid ${tokens.multipleBorderColor}`,
        },
      },
      '&:last-of-type': {
        borderRadius: {
          xs: fullWidthOnMobile
            ? 0
            : `0 0 ${tokens.borderRadius} ${tokens.borderRadius}`,
          sm: `0 0 ${tokens.borderRadius} ${tokens.borderRadius}`,
        },
        '.MuiCollapse-root': {
          borderRadius: `0 0 ${tokens.borderRadius} ${tokens.borderRadius}`,
        },
      },
      '&:last-of-type&:first-of-type': {
        borderRadius: {
          xs: fullWidthOnMobile ? 0 : tokens.borderRadius,
          sm: tokens.borderRadius,
        },
        '.MuiCollapse-root': {
          borderRadius: tokens.borderRadius,
        },
      },
      '.MuiAccordionSummary-root': {
        borderTop:
          props.variant === 'elevation' && multiple
            ? `1px solid ${tokens.multipleBorderColor}`
            : 'none',
      },
    },
  };

  const workflowSxOverrides = {
    '&:before': {
      display: 'none',
      border: 'none',
    },
    '&.MuiAccordion-root': {
      border:
        state === 'error'
          ? `1px solid ${theme.palette.negative40}`
          : `1px solid ${tokens.multipleBorderColor}`,
    },
    '&:last-of-type': {
      borderRadius: `${tokens.borderRadius} !important`,
      '.MuiCollapse-root': {
        borderRadius: `${tokens.borderRadius} !important`,
      },
    },
    '&:last-of-type&:first-of-type': {
      borderRadius: `${tokens.borderRadius} !important`,
      '.MuiCollapse-root': {
        borderRadius: `${tokens.borderRadius} !important`,
      },
    },
    '&:first-of-type': {
      borderRadius: `${tokens.borderRadius} !important`,
      '.MuiCollapse-root': {
        borderRadius: `${tokens.borderRadius} !important`,
      },
    },
  };

  const mergedProps = {
    ...props,
    ...{
      sx: [
        { ...defaultAccordionSx },
        ...(props.variant === 'workflow' ? [workflowSxOverrides] : []),
        ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
      ],
    },
    disableGutters: true,
  };
  return <MuiAccordion {...(mergedProps as MuiAccordionProps)} />;
};
export const AccordionDetails = (props: AccordionDetailsProps) => (
  <MuiAccordionDetails {...props} />
);
export const AccordionSummary = (props: AccordionSummaryProps) => (
  <MuiAccordionSummary {...props} />
);
